import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { Paper, Box, TableContainer, Table as MuiTable, useMediaQuery, useTheme, LinearProgress, CircularProgress, Typography } from '@mui/material';

// Hooks
import { useTablePersist } from './hooks/useTablePersist';
import { useTableState } from './hooks/useTableState';
import { useDebounced } from './hooks/useDebounced';
import { useColorMode } from './hooks/useColorMode';

// Components
import { TableToolbar } from './components/TableToolbar';
import { TableHeader } from './components/TableHeader';
import { TableBody } from './components/TableBody';
import { TablePagination } from './components/TablePagination';
import { MobileCardRow } from './components/MobileCardRow';
import { FloatingTableActions } from './components/FloatingTableActions';
import { RowActions } from '../TableRowComponents';

// Utils
import { COLUMN_LABELS, DEFAULT_STICKY_WIDTHS } from './utils/constants';
import { getRowKey } from './utils/formatters';

export const DataTable = ({
  dataTable,
  buttons,
  leftButtons,
  rightButtons,
  enableSorting = true,
  enableFiltering = true,
  hiddenFields = [],
  onToggleActivo,
  canToggleActivo = false,
  density = 'medium',
  enableColumnVisibility = true,
  storageKey,
  loading = false,
  enableGlobalSearch = true,
  renderSummary,
  stickyActions = false,
  maxInlineActions = 2,
  compactActions = false,
  mobileCard = true,
  columnLabels = {},
  initialColumnOrder = null,
  persistEnabled = false,
  persistKey,
  persistVista,
  usuarioId,
  empresaId,
  stickyLeftKeys = ['__expand__', 'ID', 'FOLIO'],
  stickyLeftWidths = { __expand__: 44 },
  desktopMaxHeight,
  apiUrl,
  onRowClick,
  onAdminStatusClick,
  selectable = false,
  onSelectionChange,
  selectedRows = null,
  infiniteScroll = false,
  onEndReached,
  onStateChange,
  selectionActions,
  selectionSummary,
  serverPagination = false,
  totalServerRows = 0,
  onServerPaginationChange,
  softLoading = false,
  emptyStateData = null,
  dataForExport,
  handleExportFetchAll,
  exportLabelsExtended,
  hiddenFieldsMemo,
  loadingData,
  ExportButton,
  renderFooterRow = null,
  renderMobileFooter = null
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { colorMode, setColorMode } = useColorMode();

  // Track window width so sticky column widths recalculate on resize (optimizado con rAF)
  const [windowWidth, setWindowWidth] = React.useState(
    typeof window !== 'undefined' ? window.innerWidth : 1366
  );
  React.useEffect(() => {
    let rAF = null;
    const handleResize = () => {
      if (rAF) cancelAnimationFrame(rAF);
      rAF = requestAnimationFrame(() => {
        setWindowWidth(window.innerWidth);
      });
    };
    window.addEventListener('resize', handleResize, { passive: true });
    return () => {
      if (rAF) cancelAnimationFrame(rAF);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const normalizedUsuarioId = usuarioId?.id ?? usuarioId;
  const normalizedEmpresaId = empresaId?.id ?? empresaId;
  const rawContenido = Array.isArray(dataTable) ? dataTable : dataTable?.contenido;
  const rows = useMemo(() => {
    if (Array.isArray(rawContenido)) return rawContenido;
    return [];
  }, [rawContenido]);

  const desktopBaseKeys = useMemo(() => {
    const first = rows?.[0] || {};
    const fromFirst = Object.keys(first).filter((k) => k !== 'partidas');
    const union = new Set(fromFirst);
    for (const r of rows) {
      for (const k of Object.keys(r)) {
        if (k !== 'partidas' && !union.has(k)) union.add(k);
      }
    }
    return Array.from(union);
  }, [rows]);

  const labels = useMemo(() => ({ ...COLUMN_LABELS, ...columnLabels }), [columnLabels]);

  const {
    isLoaded,
    hiddenCols, setHiddenCols,
    columnOrder, setColumnOrder,
    pinnedCols, setPinnedCols,
    sortConfig, setSortConfig,
    columnFilters, setColumnFilters,
    globalQuery, setGlobalQuery,
    rowsPerPage, setRowsPerPage
  } = useTablePersist({
    storageKey,
    persistEnabled,
    persistKey,
    persistVista,
    desktopBaseKeys,
    initialColumnOrder,
    normalizedUsuarioId,
    normalizedEmpresaId,
    hiddenFields
  });

  // Sincronizar serverPagination sólo si el rowsPerPage cargado de la BD es distinto al que ya tenía la tabla
  const prevRowsPerPageRef = useRef(rowsPerPage);
  useEffect(() => {
    if (isLoaded && onServerPaginationChange && typeof onServerPaginationChange === 'function') {
      if (prevRowsPerPageRef.current !== rowsPerPage) {
        prevRowsPerPageRef.current = rowsPerPage;
        onServerPaginationChange({ page: 1, pageSize: rowsPerPage });
      }
    }
  }, [isLoaded, rowsPerPage, onServerPaginationChange]);

  const {
    page, setPage,
    expandedRowKey, setExpandedRowKey,
    setIsPaginationChange,
    paginatedData,
    sortedData,
    safePageCount,
    orderedKeysDesktop,
  } = useTableState({
    rows,
    columnOrder,
    hiddenCols,
    pinnedCols,
    enableFiltering,
    enableGlobalSearch: false,
    enableSorting,
    sortConfig,
    columnFilters,
    globalQuery,
    rowsPerPage,
    isMobile,
    serverPagination,
    serverTotalRows: totalServerRows
  });

  const onSortWrap = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleColumnFilterChange = (field, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const containerRef = useRef(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [pageChanging, setPageChanging] = useState(false);

  // Cuando cambia la página en modo cliente (sin serverPagination), desactivamos pageChanging de inmediato
  useEffect(() => {
    if (!serverPagination) {
      setPageChanging(false);
    }
  }, [page, serverPagination]);

  // Cuando la prop loading o las filas cambian (los datos llegaron), apagamos el estado instantáneo
  useEffect(() => {
    if (!loading && rows.length > 0) {
      setPageChanging(false);
    }
  }, [loading, rows]);

  const effectiveLoading = Boolean(loading || pageChanging);

  // Auto-scroll to top of table when page changes, sin mover toda la ventana
  useEffect(() => {
    if (containerRef.current) {
      const windowScrollY = window.scrollY;
      containerRef.current.scrollTo({ top: 0, behavior: 'instant' });
      window.scrollTo({ top: windowScrollY, behavior: 'instant' });
    }
  }, [page]);

  // ─── RENDERIZADO PROGRESIVO ─────────────────────────────────────────────────
  // Aunque la página traiga 100, 500 o "Todos" (5000), solo montamos en el DOM
  // un bloque de filas y vamos agregando más conforme el usuario se desplaza.
  // Los datos completos siguen disponibles (orden, búsqueda, exportar, totales).
  const RENDER_CHUNK = 50;
  const sourceRows = infiniteScroll ? rows : paginatedData;
  const [renderState, setRenderState] = useState({ src: null, count: RENDER_CHUNK });

  // "Recarga suave": mismos datos refrescados (ej. después de guardar/editar/activar).
  // Se conserva lo ya mostrado y la posición del scroll. Si cambió el conjunto
  // (filtro, búsqueda, orden, página) se reinicia al primer bloque y arriba.
  const isSoftReload = (prev, next) =>
    Array.isArray(prev) && Array.isArray(next) && prev.length > 0 && next.length > 0 &&
    Math.abs(prev.length - next.length) <= 5 &&
    String(getRowKey(prev[0], 0)) === String(getRowKey(next[0], 0));

  const computeCount = (state, src) => {
    if (state.src === src) return state.count;
    if (isSoftReload(state.src, src)) return Math.max(RENDER_CHUNK, Math.min(state.count, src.length));
    return RENDER_CHUNK;
  };

  const renderCount = computeCount(renderState, sourceRows);
  const visibleRows = useMemo(
    () => (sourceRows.length > renderCount ? sourceRows.slice(0, renderCount) : sourceRows),
    [sourceRows, renderCount]
  );
  const hasMoreToRender = renderCount < sourceRows.length;

  const renderMore = useCallback(() => {
    setRenderState(prev => {
      const base = computeCount(prev, sourceRows);
      if (base >= sourceRows.length) return prev.src === sourceRows ? prev : { src: sourceRows, count: base };
      return { src: sourceRows, count: Math.min(base + RENDER_CHUNK, sourceRows.length) };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceRows]);

  // Al filtrar/buscar/ordenar regresamos el scroll arriba para ver los primeros resultados
  const prevSourceRef = useRef(sourceRows);
  useEffect(() => {
    const prev = prevSourceRef.current;
    prevSourceRef.current = sourceRows;
    if (prev === sourceRows) return;
    // Conservar el conteo ya mostrado en recargas suaves
    if (isSoftReload(prev, sourceRows)) {
      setRenderState(s => ({ src: sourceRows, count: computeCount({ src: prev, count: computeCount(s, prev) }, sourceRows) }));
      return;
    }
    if (containerRef.current && containerRef.current.scrollTop > 0) {
      containerRef.current.scrollTo({ top: 0, behavior: 'instant' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceRows]);

  const scrollRafRef = useRef(null);
  const handleContainerScroll = useCallback(() => {
    if (!hasMoreToRender || scrollRafRef.current) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null;
      const el = containerRef.current;
      if (!el) return;
      // Cargar el siguiente bloque cuando falten ~600px para llegar al final
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 600) renderMore();
    });
  }, [hasMoreToRender, renderMore]);

  // Si el bloque actual no alcanza a llenar la pantalla (monitores grandes), cargar otro
  useEffect(() => {
    if (!hasMoreToRender) return;
    const el = containerRef.current;
    if (el && el.scrollHeight <= el.clientHeight + 600) {
      const t = setTimeout(renderMore, 30);
      return () => clearTimeout(t);
    }
  }, [visibleRows, hasMoreToRender, renderMore]);

  useEffect(() => () => {
    if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(prev => Math.min(prev + 1, sortedData.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && focusedIndex >= 0) {
      const row = sortedData[focusedIndex];
      if (row && onRowClick) onRowClick(row);
    }
  };

  // Auto-scroll when focusedIndex changes
  useEffect(() => {
    if (focusedIndex >= renderCount - 1 && hasMoreToRender) renderMore();
    if (focusedIndex >= 0 && containerRef.current) {
      const rowElement = containerRef.current.querySelector(`[data-row-index="${focusedIndex}"]`);
      if (rowElement) {
        rowElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [focusedIndex]);

  // Reset focus when data changes
  useEffect(() => {
    setFocusedIndex(-1);
  }, [sortedData]);

  // Optimized row lookup to avoid O(n) filtering on every selection change
  const rowsMap = useMemo(() => {
    const map = new Map();
    rows.forEach(r => {
      map.set(String(getRowKey(r)), r);
    });
    return map;
  }, [rows]);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const lastIdsRef = useRef('');

  // Sync internal selection with prop (Controlled mode)
  useEffect(() => {
    if (selectedRows === null) return;
    const newIds = new Set(selectedRows.map(r => String(getRowKey(r))));
    const newIdsStr = Array.from(newIds).sort().join(',');
    if (newIdsStr !== lastIdsRef.current) {
      lastIdsRef.current = newIdsStr;
      setSelectedIds(newIds);
    }
  }, [selectedRows]);

  // Notify parent of changes
  useEffect(() => {
    if (!onSelectionChange || typeof onSelectionChange !== 'function') return;
    const currentIdsStr = Array.from(selectedIds).sort().join(',');
    if (currentIdsStr === lastIdsRef.current) return;
    lastIdsRef.current = currentIdsStr;
    const selectedArr = [];
    selectedIds.forEach(id => {
      const row = rowsMap.get(id);
      if (row) selectedArr.push(row);
    });
    onSelectionChange(selectedArr);
  }, [selectedIds, rowsMap, onSelectionChange]);

  const handleSelectAll = useCallback((e) => {
    if (e.target.checked) setSelectedIds(new Set(sortedData.map(r => String(getRowKey(r)))));
    else setSelectedIds(new Set());
  }, [sortedData]);

  const handleSelectRow = useCallback((e, id) => {
    e.stopPropagation();
    const idStr = String(id);
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(idStr)) next.delete(idStr); else next.add(idStr);
      return next;
    });
  }, []);

  const hasPartidas = !!dataTable?.hasPartidas || rows.some(r => r.partidas && r.partidas.length > 0);

  const stickyLeftMeta = useMemo(() => {
    if (isMobile) return null;
    const meta = {};
    let currentLeft = 0;

    const dynamicPinned = Array.from(pinnedCols).filter(k => orderedKeysDesktop.includes(k));
    const initialSpecial = [];
    if (selectable) initialSpecial.push('__select__');
    if (hasPartidas) initialSpecial.push('__expand__');

    const allStickyKeys = new Set([...initialSpecial, ...stickyLeftKeys, ...dynamicPinned]);
    const sortedSticky = [];
    if (selectable) sortedSticky.push('__select__');
    if (hasPartidas) sortedSticky.push('__expand__');
    orderedKeysDesktop.forEach(k => {
      if (allStickyKeys.has(k)) sortedSticky.push(k);
    });

    sortedSticky.forEach((key) => {
      const getResponsiveWidth = (key) => {
        if (stickyLeftWidths[key]) return stickyLeftWidths[key];
        switch (key) {
          case '__select__': return 34;
          case '__expand__': return 32;
          case 'ID':         return 60;
          case 'FOLIO':      return 85;
          case 'OC_REQUI':   return 90;
          default:           return 95;
        }
      };
      const w = getResponsiveWidth(key);
      meta[key] = {
        left: currentLeft,
        width: w,
        zIndex: 10,
        isLastSticky: false,
        isPinned: pinnedCols.has(key)
      };
      currentLeft += w;
    });

    const metaKeys = Object.keys(meta);
    if (metaKeys.length > 0) {
      meta[metaKeys[metaKeys.length - 1]].isLastSticky = true;
    }
    return meta;
  }, [isMobile, stickyLeftKeys, orderedKeysDesktop, stickyLeftWidths, hasPartidas, selectable, pinnedCols, windowWidth]);

  const hasActions = !!buttons || !!onToggleActivo;
  const actionsColumnWidth = compactActions
    ? Math.max(42, Math.min(maxInlineActions || 1, 4) * 28 + 14)
    : undefined;
  const renderRowActions = useMemo(() => {
    if (!hasActions) return null;
    return (row) => (
      <RowActions
        row={row}
        buttons={buttons}
        onToggleActivo={onToggleActivo}
        canToggleActivo={canToggleActivo}
        maxInline={maxInlineActions || 10}
        compact={compactActions}
      />
    );
  }, [hasActions, buttons, onToggleActivo, canToggleActivo, maxInlineActions, compactActions]);

  const activeFilterCount = useMemo(() => {
    return Object.values(columnFilters || {}).filter(v => v !== '' && v != null).length;
  }, [columnFilters]);

  const debouncedGlobalQuery = useDebounced(globalQuery, 500);

  useEffect(() => {
    if (onStateChange && typeof onStateChange === 'function') {
      onStateChange({
        globalQuery: debouncedGlobalQuery,
        sortConfig,
        page,
        rowsPerPage,
        columnFilters,
        hiddenCols: Array.from(hiddenCols)
      });
    }
  }, [debouncedGlobalQuery, sortConfig, page, rowsPerPage, columnFilters, hiddenCols, onStateChange]);

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        border: '1px solid #cbd5e1',
        borderRadius: '12px',
        boxShadow: '0 10px 30px -5px rgba(15, 23, 42, 0.08), 0 4px 6px -2px rgba(15, 23, 42, 0.03)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        height: '100%',
        backgroundColor: '#ffffff'
      }}
    >
      <TableToolbar
        leftButtons={leftButtons}
        rightButtons={
          <>
            {ExportButton && (
              <ExportButton
                data={dataForExport}
                onFetchAll={handleExportFetchAll}
                columnLabels={exportLabelsExtended}
                hiddenFields={hiddenFieldsMemo}
                fileName="Exportacion.xlsx"
                tableLoading={loadingData}
              />
            )}
            {rightButtons}
          </>
        }
        enableColumnVisibility={enableColumnVisibility}
        columnOrder={columnOrder}
        setColumnOrder={setColumnOrder}
        hiddenCols={hiddenCols}
        setHiddenCols={setHiddenCols}
        pinnedCols={pinnedCols}
        setPinnedCols={setPinnedCols}
        labels={labels}
        activeFilterCount={activeFilterCount}
        isMobile={isMobile}
        enableGlobalSearch={enableGlobalSearch}
        globalQuery={globalQuery}
        setGlobalQuery={setGlobalQuery}
        numSelected={selectedIds.size}
        selectionActions={selectionActions}
        selectionSummary={selectionSummary}
        hiddenFields={hiddenFields}
        colorMode={colorMode}
        setColorMode={setColorMode}
      />

      <Box sx={{ flexGrow: 1, overflow: 'hidden', position: 'relative' }}>
        {/* Progress bar at top edge */}
        {loading && (
          <LinearProgress
            sx={{
              position: 'absolute', top: 0, left: 0, right: 0, zIndex: 20,
              height: 3,
              backgroundColor: 'rgba(6,85,165,0.12)',
              '& .MuiLinearProgress-bar': { backgroundColor: '#0655A5' },
            }}
          />
        )}

        {/* Spinner overlay al cargar datos — solo en carga inicial, NO al paginar */}
        {loading && rows.length > 0 && !softLoading && !pageChanging && (
          <Box
            sx={{
              position: 'absolute', inset: 0, zIndex: 15,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.65)',
              backdropFilter: 'blur(1px)',
              gap: 1.5,
            }}
          >
            <CircularProgress size={36} thickness={4} sx={{ color: '#0655A5' }} />
            <Typography fontSize="0.78rem" fontWeight={600} color="text.secondary">
              Cargando datos...
            </Typography>
          </Box>
        )}

        <TableContainer
          ref={containerRef}
          tabIndex={0}
          onKeyDown={handleKeyDown}
          onScroll={handleContainerScroll}
          sx={{
            height: '100%',
            maxHeight: '100%',
            overflowX: 'auto',
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            overscrollBehaviorY: 'contain',
            scrollbarGutter: 'stable both-edges',
            minHeight: 200,
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            outline: 'none',
            '&::-webkit-scrollbar': {
              width: 8,
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.02)',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.18)',
              borderRadius: 4,
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.32)',
              },
            },
          }}
        >
          {isMobile && mobileCard ? (
            <Box sx={{ p: { xs: 1.2, sm: 2 }, pb: { xs: '110px', sm: '120px' }, backgroundColor: '#f8fafc', display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
              {visibleRows.map((row, index) => (
                <MobileCardRow
                  key={getRowKey(row, index)}
                  row={row}
                  orderedKeys={orderedKeysDesktop}
                  labels={labels}
                  renderRowActions={renderRowActions}
                  hasPartidas={Boolean((row.partidas && row.partidas.length > 0) || (row.PARTIDAS && row.PARTIDAS.length > 0))}
                  onRowClick={onRowClick}
                  onAdminStatusClick={onAdminStatusClick}
                />
              ))}

              {renderMobileFooter && renderMobileFooter()}
            </Box>
          ) : (
            <MuiTable stickyHeader size={density} sx={{ minWidth: '100%', width: '100%', tableLayout: 'auto' }}>
              <TableHeader
                orderedKeys={orderedKeysDesktop}
                stickyLeftMeta={stickyLeftMeta}
                sortConfig={sortConfig}
                onSort={onSortWrap}
                enableSorting={enableSorting}
                enableFiltering={enableFiltering}
                columnFilters={columnFilters}
                onColumnFilterChange={handleColumnFilterChange}
                labels={labels}
                renderActionsCol={hasActions}
                hasPartidas={hasPartidas}
                selectable={selectable}
                numSelected={selectedIds.size}
                rowCount={sortedData.length}
                onSelectAll={handleSelectAll}
                actionsColumnWidth={actionsColumnWidth}
              />
              <TableBody
                data={visibleRows}
                orderedKeys={orderedKeysDesktop}
                stickyLeftMeta={stickyLeftMeta}
                isLoading={effectiveLoading}
                colSpan={orderedKeysDesktop.length + (hasActions ? 1 : 0) + (selectable ? 1 : 0) + (hasPartidas ? 1 : 0)}
                onRowClick={onRowClick}
                onAdminStatusClick={onAdminStatusClick}
                hasPartidas={hasPartidas}
                expandedRowKey={expandedRowKey}
                onToggleExpand={(k) => setExpandedRowKey(prev => prev === k ? null : k)}
                renderRowActions={renderRowActions}
                compactActions={compactActions}
                actionsColumnWidth={actionsColumnWidth}
                labels={labels}
                selectable={selectable}
                selectedIds={selectedIds}
                onSelectRow={handleSelectRow}
                focusedIndex={focusedIndex}
                onFocusRow={(idx) => setFocusedIndex(idx)}
                colorMode={colorMode}
                emptyStateData={emptyStateData}
              />
              {renderFooterRow && renderFooterRow({
                orderedKeys: orderedKeysDesktop,
                stickyLeftMeta,
                hasActions,
                selectable,
                hasPartidas,
              })}
            </MuiTable>
          )}
          {hasMoreToRender && !effectiveLoading && (
            <Box
              onClick={renderMore}
              sx={{
                position: 'sticky', left: 0, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                cursor: 'pointer', color: '#64748b', fontSize: '0.72rem', fontWeight: 600,
                '&:hover': { color: '#0655A5' },
              }}
            >
              <CircularProgress size={12} thickness={5} sx={{ color: '#0655A5' }} />
              Mostrando {renderCount} de {sourceRows.length} · desliza para ver más
            </Box>
          )}
        </TableContainer>
      </Box>

      {!infiniteScroll && (
        <TablePagination
          page={page}
          count={serverPagination ? (totalServerRows || dataTable?.total || rows.length) : ((activeFilterCount > 0 || (debouncedGlobalQuery && debouncedGlobalQuery.trim() !== '')) ? sortedData.length : (totalServerRows || dataTable?.total || rows.length))}
          rowsPerPage={rowsPerPage}
          setPage={setPage}
          onRowsPerPageChange={(newSize) => {
            setRowsPerPage(newSize);
            setPage(0);
            if (onServerPaginationChange) {
              onServerPaginationChange({
                page: 1,
                pageSize: newSize
              });
            }
          }}
          safePageCount={safePageCount}
          infiniteScroll={infiniteScroll}
          onEndReached={onEndReached}
          enableColumnVisibility={enableColumnVisibility}
          columnOrder={columnOrder}
          hiddenCols={hiddenCols}
          pinnedCols={pinnedCols}
          toggleCol={(k) => {
            setHiddenCols(prev => {
              const next = new Set(prev);
              if (next.has(k)) next.delete(k); else next.add(k);
              return next;
            });
          }}
          togglePin={(k) => {
            setPinnedCols(prev => {
              const next = new Set(prev);
              if (next.has(k)) next.delete(k); else next.add(k);
              return next;
            });
          }}
          labels={labels}
          hiddenFields={hiddenFields}
          colorMode={colorMode}
          setColorMode={setColorMode}
          onPageChange={(p) => {
            setPageChanging(true);
            setIsPaginationChange(true);
            setPage(p);
            if (onServerPaginationChange) {
              onServerPaginationChange({
                page: p + 1,
                pageSize: rowsPerPage
              });
            }
            const neededEnd = (p + 1) * rowsPerPage;
            if (neededEnd > rows.length && dataTable?.hasMore && !loading) {
              if (onEndReached) onEndReached();
            }
          }}
        />
      )}

      {renderSummary && renderSummary() && (
        <Box sx={{
          px: 2,
          py: 0.75,
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          borderTop: '1px solid #e2e8f0',
          minHeight: 36,
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
        }}>
          {renderSummary()}
        </Box>
      )}
    </Paper>
  );
};
